import React, { useState, useCallback } from 'react';
import { 
  PlusIcon, 
  LinkIcon, 
  QrCodeIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  CogIcon,
  TrashIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { BookingSettings, BookingLink } from '../../types/appointments';
import { appointmentService } from '../../services/appointmentService';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import { useLanguage } from '../../contexts/LanguageContext';

interface BookingLinkGeneratorProps {
  settings: BookingSettings[];
  onSettingsChange: () => void;
}

const BookingLinkGenerator: React.FC<BookingLinkGeneratorProps> = ({
  settings,
  onSettingsChange
}) => {
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSettings, setSelectedSettings] = useState<BookingSettings | null>(null);
  const [generatedLink, setGeneratedLink] = useState<BookingLink | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateLink = useCallback(async (settingsId: string) => {
    setIsGenerating(true);
    try {
      const link = await appointmentService.createBookingLink(settingsId);
      setGeneratedLink(link);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error generating booking link:', error);
      // TODO: Show error toast
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const handleCopyUrl = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  }, []);

  const getBookingUrl = (publicId: string) => {
    return appointmentService.generateBookingUrl(publicId);
  };

  const getQRCodeUrl = (url: string) => {
    return appointmentService.generateQRCode(url);
  };

  return (
    <div className="space-y-6">
      {/* Fejléc */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Foglalási Linkek
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Hozz létre és oszd meg foglalási linkjeidet
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setIsModalOpen(true)}
          className="flex items-center space-x-2"
        >
          <PlusIcon className="h-4 w-4" />
          <span>Új Link</span>
        </Button>
      </div>

      {/* Beállítások listája */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {settings.map((setting) => (
          <div
            key={setting.id}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
          >
            {/* Beállítás fejléc */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                  {setting.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {setting.description || 'Nincs leírás'}
                </p>
              </div>
              <div className="flex items-center space-x-1 ml-2">
                <button
                  onClick={() => setSelectedSettings(setting)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Beállítások"
                >
                  <CogIcon className="h-4 w-4" />
                </button>
                <button
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Előnézet"
                >
                  <EyeIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Beállítás részletek */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Időtartam:</span>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  {setting.duration} perc
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Munkaidő:</span>
                <span className="text-gray-900 dark:text-gray-100 font-medium">
                  {setting.availableHours.start} - {setting.availableHours.end}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Státusz:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  setting.isActive 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                }`}>
                  {setting.isActive ? 'Aktív' : 'Inaktív'}
                </span>
              </div>
            </div>

            {/* Akció gombok */}
            <div className="flex items-center space-x-2">
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleGenerateLink(setting.id!)}
                disabled={isGenerating || !setting.isActive}
                className="flex-1 flex items-center justify-center space-x-1"
              >
                <LinkIcon className="h-4 w-4" />
                <span>Link Generálás</span>
              </Button>
            </div>
          </div>
        ))}

        {/* Üres állapot */}
        {settings.length === 0 && (
          <div className="col-span-full">
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
              <LinkIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                Nincs foglalási beállítás
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Hozz létre egy új foglalási beállítást a kezdéshez
              </p>
              <Button
                variant="primary"
                onClick={() => setIsModalOpen(true)}
                className="flex items-center space-x-2"
              >
                <PlusIcon className="h-4 w-4" />
                <span>Első Beállítás Létrehozása</span>
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Link generálás modal */}
      <Modal
        isOpen={isModalOpen && generatedLink !== null}
        onClose={() => {
          setIsModalOpen(false);
          setGeneratedLink(null);
        }}
        title="Foglalási Link Generálva"
        size="lg"
      >
        {generatedLink && (
          <div className="p-6 space-y-6">
            {/* Siker üzenet */}
            <div className="flex items-center space-x-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <CheckIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              <div>
                <h3 className="font-medium text-green-800 dark:text-green-200">
                  Link sikeresen létrehozva!
                </h3>
                <p className="text-sm text-green-700 dark:text-green-300">
                  Most már megoszthatod az ügyfelaiddel
                </p>
              </div>
            </div>

            {/* URL másolás */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Foglalási Link
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={getBookingUrl(generatedLink.publicId)}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyUrl(getBookingUrl(generatedLink.publicId))}
                  className="flex items-center space-x-1"
                >
                  {copiedUrl === getBookingUrl(generatedLink.publicId) ? (
                    <CheckIcon className="h-4 w-4 text-green-600" />
                  ) : (
                    <ClipboardDocumentIcon className="h-4 w-4" />
                  )}
                  <span>{copiedUrl === getBookingUrl(generatedLink.publicId) ? 'Másolva!' : 'Másolás'}</span>
                </Button>
              </div>
            </div>

            {/* QR kód */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                QR Kód
              </label>
              <div className="flex items-center justify-center p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                <img
                  src={getQRCodeUrl(getBookingUrl(generatedLink.publicId))}
                  alt="QR Code"
                  className="w-32 h-32"
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Nyomtasd ki vagy oszd meg digitálisan
              </p>
            </div>

            {/* Akció gombok */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="outline"
                onClick={() => {
                  setIsModalOpen(false);
                  setGeneratedLink(null);
                }}
              >
                Bezárás
              </Button>
              <Button
                variant="primary"
                onClick={() => window.open(getBookingUrl(generatedLink.publicId), '_blank')}
                className="flex items-center space-x-2"
              >
                <EyeIcon className="h-4 w-4" />
                <span>Előnézet</span>
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BookingLinkGenerator;
